import { makeAutoObservable, type IReactionDisposer, reaction, runInAction } from 'mobx'
import { type StripeCardElementChangeEvent, type TokenResult } from '@stripe/stripe-js'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import dayjs from 'dayjs'
import { bannerStore, type IDropdownItem, showToast } from 'shared/ui'
import { MIN_ORGANIZATION_NAME_LENGTH } from 'shared/constants/auth'
import { logger } from 'shared/lib'
import { type IParamsSignUpOrganization, type ReCaptchaStore } from 'entities/Auth'
import { type IResponseProfile } from 'entities/Users/api/types'
import { UsersApi } from 'entities/Users'
import { ChannelsProfile } from 'entities/Users/channels/profile'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { CountryEnum } from './type'

export class RegisterMultiorgStore {
  private _lastName = ''
  private _firstName = ''
  private _number = ''
  private _country: IDropdownItem = {
    label: 'United States',
    id: CountryEnum.US,
    iconL: 'flagUSA',
    labelRight: '+1',
  }
  private _email = ''
  private _organizationName = ''
  private _cardToken = ''
  private _timezone: string | null = null
  private _loading = false
  private _showRetryAlert = false
  private _cardComplete = false
  private _cardError: string | null = null
  private _preloading = true

  private _disposeOrganizationNameValidation: IReactionDisposer | null = null
  private _disposeOrganizationNameValidationTrigger: IReactionDisposer | null = null
  private _organizationNameError: string | null = null
  private _organizationNameValidationTrigger: string | null = null

  constructor(private _entities: IEntitiesStore, private _storeReCaptchaStore: ReCaptchaStore) {
    this.reactionOrganizationNameValidation()
    this.reactionOrganizationNameValidationTrigger()
    this.init()

    makeAutoObservable(this)
  }

  get storeReCaptchaStore() {
    return this._storeReCaptchaStore
  }

  get preloading() {
    return this._preloading
  }

  get organizationNameError() {
    return this._organizationNameError
  }

  get GBDropdownItem(): IDropdownItem {
    return {
      label: 'United Kingdom',
      id: CountryEnum.GB,
      iconL: 'flagUK',
      labelRight: '+44',
    }
  }

  get countries(): IDropdownItem[] {
    return [
      {
        label: 'United States',
        id: CountryEnum.US,
        iconL: 'flagUSA',
        labelRight: '+1',
      },
      {
        label: 'Canada',
        id: CountryEnum.CA,
        iconL: 'flagCanada',
        labelRight: '+1',
      },
      this.GBDropdownItem,
    ]
  }

  get disabledRegister() {
    return !this._organizationName || !!this.cardError
  }

  get email() {
    return this._email
  }

  get cardError() {
    return this._cardError
  }

  get organizationName() {
    return this._organizationName
  }

  get loading() {
    return this._loading
  }

  get timezone() {
    return this._timezone || dayjs.tz.guess()
  }

  get formattedNumber() {
    if (this.country.id === CountryEnum.GB) {
      return `+44${this.number.replace(/[^0-9]/g, '')}`
    }
    return this.number
  }

  get payload(): IParamsSignUpOrganization {
    return {
      first_name: this._firstName,
      last_name: this._lastName,
      email: this._email,
      organization_name: this._organizationName,
      token: this._cardToken,
      number: this.formattedNumber,
      country: this.country.id as string,
      timezone: this.timezone,
      number_vendor_key: this.numberVendorKey,
      plan: '',
      terms: true,
      lead_source: '',
      ga_source: '',
      ga_medium: '',
      ga_term: '',
      ga_content: '',
      ga_campaign: '',
      ga_referurl: '',
      ga_ip: '',
      city: '',
      region: '',
      multiorganization_registration: true,
      crm_id: 0,
      smsCode: '',
      utm_campaign: '',
      utm_content: '',
      utm_medium: '',
      utm_source: '',
      coupon: '',
      noCardTest: false,
      new_billing: true,
      answer_country: '',
      answer_team_size: '',
      answer_integration: '',
      ...this._storeReCaptchaStore.params,
    }
  }

  get showRetryAlert() {
    return this._showRetryAlert
  }

  get firstName() {
    return this._firstName
  }

  get lastName() {
    return this._lastName
  }

  get number() {
    return this._number
  }

  get country() {
    return this._country
  }

  get isValidOrganizationName() {
    return this._organizationName.length >= MIN_ORGANIZATION_NAME_LENGTH
  }

  get numberVendorKey() {
    const searchParams = new URLSearchParams(window.location.search)
    if (searchParams.has('twilio')) return 'twilio-integration'
    return ''
  }

  init = async () => {
    bannerStore.removeAll()
    try {
      runInAction(() => {
        this._preloading = true
      })

      const { data } = await UsersApi.getUsersProfile()
      new ChannelsProfile(this._entities.websocket).subscribeChannels(data.id)
      runInAction(() => this.initData(data))
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._preloading = false
      })
    }
  }

  clearCardError() {
    this._cardError = null
  }

  clearErrors = () => {
    if (this._organizationNameError) this._organizationNameError = ''
  }

  setCardError(value: string | null) {
    this._cardError = value
  }

  onChangeCard = (event: StripeCardElementChangeEvent) => {
    this._cardComplete = event.complete
    this._cardError = event.error?.message || null
  }

  setShowRetryError = (value: boolean) => {
    this._showRetryAlert = value
  }

  setOrganizationName = (value: string) => {
    if (this._organizationNameError) this._organizationNameError = null
    this._organizationName = value
  }

  initData(data: IResponseProfile) {
    this._email = data.email
    this._firstName = data.first_name
    this._lastName = data.last_name

    if (data.country === CountryEnum.GB && data.number.startsWith('+44')) {
      this._number = data.number.slice(3)
      this._country = this.GBDropdownItem
    } else {
      this._number = data.formatted_number.replace(/[^-.0-9]/g, '')
    }
  }

  private _onSubmitCard: (() => Promise<TokenResult | undefined>) | null = null

  setOnSubmit = (onSubmit: () => Promise<TokenResult | undefined>) => {
    this._onSubmitCard = onSubmit
  }

  onAddCard = async () => {
    if (this._cardError) return
    if (this._onSubmitCard) {
      try {
        const res = await this._onSubmitCard()
        if (!res || res?.error) {
          runInAction(() => {
            this.setCardError(res?.error.message || 'Please provide another credit card')
          })
          return
        }

        this._cardToken = res.token.id
        this.setCardError(null)
      } catch (e) {
        logger.error(e)
      }
    }
  }

  onRegister = async () => {
    try {
      this.setLoading(true)
      await this.onAddCard()

      await this._storeReCaptchaStore.setExecuteRecaptcha('sign_up_organization_create')
      await this._entities.authStore.signUpOrganization(this.payload, {
        reCaptchaStore: this._storeReCaptchaStore,
      })
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 're_captcha_v2_hidden') {
          this.setLoading(false)
        }
      }

      if (error instanceof AxiosError) {
        logger.error(error)
        this.setLoading(false)

        if (error.response?.status === 500) this.setShowRetryError(true)

        if (this._storeReCaptchaStore.checkServerError(error.errors.getError('message'))) return

        error.errors.values().forEach((msg) => {
          showToast({ type: 'error', title: msg.value })
        })
      }
    }
  }

  setLoading = (value: boolean) => {
    this._loading = value
  }

  triggerOrganizationNameValidation = () => {
    this._organizationNameValidationTrigger = nanoid()
  }

  reactionOrganizationNameValidation = () => {
    this._disposeOrganizationNameValidation?.()
    this._disposeOrganizationNameValidation = reaction(
      () => [this._organizationNameValidationTrigger, this.isValidOrganizationName],
      (isValid) => {
        if (isValid) this.clearErrors()
      },
      {
        fireImmediately: true,
      }
    )
  }

  reactionOrganizationNameValidationTrigger = () => {
    this._disposeOrganizationNameValidationTrigger?.()
    this._disposeOrganizationNameValidationTrigger = reaction(
      () => this._organizationNameValidationTrigger,
      (value) => {
        this._organizationNameError =
          value && !this.isValidOrganizationName
            ? `Organization name must contain at least ${MIN_ORGANIZATION_NAME_LENGTH} characters`
            : null
        if (this.isValidOrganizationName) {
          this.clearErrors()
          if (document.activeElement instanceof HTMLElement) document.activeElement.blur()
          this.onRegister()
        }
      },
      {
        fireImmediately: true,
      }
    )
  }
}
